import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import request from "../../../utils/request";
import { ENFORCED_TRACKING, DEFAULT_SEQUENCE } from "../constants";

const DEFAULT_FILTERS = {
  q: "",
  stage: "",
  status: "",
  tags: "",
  needsAction: false,
};

const PAGE_SIZE = 25;

export default function useLeadsWorkspace() {
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  const [overview, setOverview] = useState(null);
  const [memberRows, setMemberRows] = useState([]);
  const [queueRows, setQueueRows] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [workspaceError, setWorkspaceError] = useState("");

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [selectedLeadRow, setSelectedLeadRow] = useState(null);
  const [selectedLeadDetail, setSelectedLeadDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Prevents stale responses from overwriting newer ones when switching
  // campaigns or typing quickly.
  const requestTokenRef = useRef(0);

  const activeCampaign = useMemo(
    () => campaigns.find((campaign) => campaign._id === activeCampaignId) || null,
    [campaigns, activeCampaignId]
  );

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const { data } = await request.get("/campaigns", { withCredentials: true });
      const nextCampaigns = data.campaigns || [];
      setCampaigns(nextCampaigns);
      setActiveCampaignId((current) => {
        if (current && nextCampaigns.some((c) => c._id === current)) return current;
        return nextCampaigns[0]?._id || null;
      });
    } catch (error) {
      console.error("Failed to load campaigns", error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const loadWorkspace = useCallback(
    async (campaignId, nextPage = 1, nextFilters = filters) => {
      if (!campaignId) return;
      const token = ++requestTokenRef.current;

      setLoadingWorkspace(true);
      setWorkspaceError("");

      try {
        const params = {
          page: nextPage,
          limit: PAGE_SIZE,
          q: nextFilters.q || undefined,
          stage: nextFilters.stage || undefined,
          status: nextFilters.status || undefined,
          tags: nextFilters.tags || undefined,
          needsAction: nextFilters.needsAction || undefined,
        };

        const [overviewRes, membersRes, queueRes, activityRes] = await Promise.all([
          request.get(`/campaigns/${campaignId}/overview`, { withCredentials: true }),
          request.get(`/campaigns/${campaignId}/leads`, { params, withCredentials: true }),
          request.get(`/campaigns/${campaignId}/queue`, { withCredentials: true }),
          request.get(`/campaigns/${campaignId}/activity`, { withCredentials: true }),
        ]);

        if (requestTokenRef.current !== token) return;

        setOverview(overviewRes.data);
        setMemberRows(membersRes.data.leads || []);
        setPagination(
          membersRes.data.pagination || { page: 1, limit: PAGE_SIZE, total: 0, pages: 1 }
        );
        setQueueRows(queueRes.data.queue || []);
        setActivityRows(activityRes.data.activities || []);
      } catch (error) {
        console.error("Failed to load campaign workspace", error);
        if (requestTokenRef.current === token) {
          setWorkspaceError("Could not load the campaign workspace. Try refreshing.");
        }
      } finally {
        if (requestTokenRef.current === token) {
          setLoadingWorkspace(false);
        }
      }
    },
    [filters]
  );

  const loadLeadDetail = useCallback(async (leadId) => {
    if (!leadId) return;
    setLoadingDetail(true);
    try {
      const { data } = await request.get(`/leads/${leadId}`, { withCredentials: true });
      setSelectedLeadDetail({
        lead: data.lead,
        activities: data.activities || [],
        campaigns: data.campaigns || [],
      });
    } catch (error) {
      console.error("Failed to load lead detail", error);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Debounce filter-driven reloads so typing in search doesn't hammer the API.
  useEffect(() => {
    if (!activeCampaignId) return undefined;
    const handle = setTimeout(() => {
      loadWorkspace(activeCampaignId, page, filters);
    }, 180);
    return () => clearTimeout(handle);
    // We want to re-run when the campaign, page, or any filter changes.
    // `loadWorkspace` depends on filters but is stable otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaignId, page, filters]);

  // Reset pagination when filters or campaign change.
  useEffect(() => {
    setPage(1);
  }, [activeCampaignId, filters.q, filters.stage, filters.status, filters.tags, filters.needsAction]);

  const refreshActiveCampaign = useCallback(async () => {
    if (!activeCampaignId) return;
    await Promise.all([loadCampaigns(), loadWorkspace(activeCampaignId, page, filters)]);
    if (selectedLeadDetail?.lead?._id) {
      await loadLeadDetail(selectedLeadDetail.lead._id);
    }
  }, [
    activeCampaignId,
    filters,
    loadCampaigns,
    loadLeadDetail,
    loadWorkspace,
    page,
    selectedLeadDetail,
  ]);

  const createCampaign = useCallback(
    async ({ name, description }) => {
      if (!name?.trim()) return null;
      const { data } = await request.post(
        "/campaigns",
        {
          name: name.trim(),
          description: (description || "").trim(),
          status: "active",
          tracking: { ...ENFORCED_TRACKING },
          sequenceSteps: DEFAULT_SEQUENCE,
        },
        { withCredentials: true }
      );
      await loadCampaigns();
      if (data.campaign?._id) {
        setActiveCampaignId(data.campaign._id);
      }
      return data.campaign || null;
    },
    [loadCampaigns]
  );

  const patchCampaign = useCallback(
    async (campaignId, patch) => {
      const payload = {
        ...patch,
        tracking: { ...(patch.tracking || {}), ...ENFORCED_TRACKING },
      };
      await request.patch(`/campaigns/${campaignId}`, payload, { withCredentials: true });
      await refreshActiveCampaign();
    },
    [refreshActiveCampaign]
  );

  const patchCampaignLead = useCallback(
    async (campaignLeadId, patch, { silent = false } = {}) => {
      if (!campaignLeadId) return;
      // Optimistic update for snappy feel on stage/enrollment toggles.
      setMemberRows((current) =>
        current.map((row) =>
          row.campaignLead?._id === campaignLeadId
            ? { ...row, campaignLead: { ...row.campaignLead, ...patch } }
            : row
        )
      );
      try {
        await request.patch(`/campaign-leads/${campaignLeadId}`, patch, { withCredentials: true });
        if (!silent) await refreshActiveCampaign();
      } catch (error) {
        console.error("Failed to update campaign lead", error);
        await refreshActiveCampaign();
        throw error;
      }
    },
    [refreshActiveCampaign]
  );

  const queueAction = useCallback(
    async (action, ids) => {
      if (!ids?.length) return;
      setLoadingWorkspace(true);
      try {
        await Promise.all(
          ids.map((id) =>
            request.post(`/campaign-queue/${id}/${action}`, {}, { withCredentials: true })
          )
        );
        await refreshActiveCampaign();
      } catch (error) {
        console.error(`Failed to ${action} queue item`, error);
        throw error;
      } finally {
        setLoadingWorkspace(false);
      }
    },
    [refreshActiveCampaign]
  );

  const saveSegment = useCallback(
    async (name) => {
      if (!activeCampaignId || !activeCampaign || !name?.trim()) return;
      const savedSegments = [
        ...(activeCampaign.savedSegments || []),
        { name: name.trim(), filters: { ...filters } },
      ];
      await request.patch(
        `/campaigns/${activeCampaignId}`,
        { savedSegments },
        { withCredentials: true }
      );
      await refreshActiveCampaign();
    },
    [activeCampaign, activeCampaignId, filters, refreshActiveCampaign]
  );

  const deleteSegment = useCallback(
    async (segmentKey) => {
      if (!activeCampaignId || !activeCampaign) return;
      const savedSegments = (activeCampaign.savedSegments || []).filter((segment) => {
        const key = segment._id || segment.name;
        return key !== segmentKey;
      });
      await request.patch(
        `/campaigns/${activeCampaignId}`,
        { savedSegments },
        { withCredentials: true }
      );
      await refreshActiveCampaign();
    },
    [activeCampaign, activeCampaignId, refreshActiveCampaign]
  );

  const saveLeadNotes = useCallback(async (leadId, notes) => {
    if (!leadId) return null;
    const { data } = await request.post(
      `/leads/${leadId}/notes`,
      { notes },
      { withCredentials: true }
    );
    setSelectedLeadDetail((current) =>
      current && current.lead?._id === leadId
        ? { ...current, lead: data.lead }
        : current
    );
    return data.lead;
  }, []);

  const openLead = useCallback(
    async (row) => {
      setSelectedLeadRow(row);
      if (row?.lead?._id) {
        await loadLeadDetail(row.lead._id);
      }
    },
    [loadLeadDetail]
  );

  const closeLead = useCallback(() => {
    setSelectedLeadRow(null);
    setSelectedLeadDetail(null);
  }, []);

  return {
    // data
    campaigns,
    activeCampaign,
    activeCampaignId,
    overview,
    memberRows,
    queueRows,
    activityRows,
    pagination,
    page,
    filters,
    selectedLeadRow,
    selectedLeadDetail,

    // loading
    loadingCampaigns,
    loadingWorkspace,
    loadingDetail,
    workspaceError,

    // setters
    setActiveCampaignId,
    setFilters,
    setPage,
    setSelectedLeadRow,

    // actions
    refreshActiveCampaign,
    createCampaign,
    patchCampaign,
    patchCampaignLead,
    queueAction,
    saveSegment,
    deleteSegment,
    saveLeadNotes,
    openLead,
    closeLead,
    loadLeadDetail,
  };
}
